import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDatabase } from '../contexts/DatabaseContext';
import { useTenant } from '../contexts/TenantContext';
import { useTenantTrades } from '../hooks/useTenantTrades';
import { FormService } from '../services/FormService';
import HighDensityDataGrid from '../components/common/HighDensityDataGrid';
import CertificateQuickViewDrawer from '../components/modals/CertificateQuickViewDrawer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiTrendingUp,
  FiPlus,
  FiArrowRight,
  FiZap,
  FiLayers
} = FiIcons;

export const Dashboard = () => {
  const { isReady, error: dbError } = useDatabase();
  const { activeTenant } = useTenant();
  const navigate = useNavigate();
  const { allowedForms } = useTenantTrades();

  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    drafts: 0,
    completed: 0,
    thisMonth: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Trade-gated quick start generators (tenant's enabled trades only)
  const availableForms = allowedForms;

  useEffect(() => {
    if (isReady) {
      loadDashboardData();
    }
  }, [isReady]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [recentData, statsData] = await Promise.all([
        FormService.getRecentForms(25),
        FormService.getFormStats()
      ]);
      setRecords(recentData || []);
      setStats(statsData || { total: 0, drafts: 0, completed: 0, thisMonth: 0 });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInspectRecord = (record) => {
    setSelectedCertificate(record);
    setIsDrawerOpen(true);
  };

  const handleEditRecord = (record) => {
    navigate(`/forms/${record.formId}/${record.id}`);
  };

  const handleDeleteRecord = async (id) => {
    try {
      await FormService.deleteForm(id);
      loadDashboardData();
    } catch (e) {
      console.error('Failed to delete form:', e);
    }
  };

  const handleBatchStatusChange = async (ids, newStatus) => {
    try {
      await Promise.all(ids.map((id) => FormService.updateFormStatus(id, newStatus)));
      loadDashboardData();
    } catch (e) {
      console.error('Failed batch status update:', e);
    }
  };

  const handleBatchDelete = async (ids) => {
    try {
      await Promise.all(ids.map((id) => FormService.deleteForm(id)));
      loadDashboardData();
    } catch (e) {
      console.error('Failed batch delete:', e);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Welcome Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 rounded-2xl shadow-xl border border-slate-800">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-500/20 text-blue-300 border border-blue-400/30">
              {activeTenant.name}
            </span>
            <span className="text-xs text-slate-400">• {activeTenant.plan}</span>
          </div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight text-white">
            Compliance Certificate Control Center
          </h1>
          <p className="text-xs md:text-sm text-slate-300">
            Offline-first statutory compliance management, real-time validations, and rapid PDF dispatch.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/forms')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow-lg transition-all flex items-center gap-2"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Create Certificate</span>
          </button>
        </div>
      </div>

      {/* KPI Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Forms */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Total Certificates
            </p>
            <p className="text-2xl font-black text-slate-900 dark:text-slate-100 mt-1">{stats.total}</p>
            <span className="text-[11px] text-slate-400 font-medium">All recorded compliance forms</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <SafeIcon icon={FiFileText} className="w-6 h-6" />
          </div>
        </div>

        {/* Drafts */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Drafts in Progress
            </p>
            <p className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">{stats.drafts}</p>
            <span className="text-[11px] text-amber-500 font-medium">Pending engineer signature</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <SafeIcon icon={FiClock} className="w-6 h-6" />
          </div>
        </div>

        {/* Completed */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Issued & Certified
            </p>
            <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{stats.completed}</p>
            <span className="text-[11px] text-emerald-500 font-medium">Locked & PDF Generated</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
          </div>
        </div>

        {/* This Month */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Issued This Month
            </p>
            <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400 mt-1">{stats.thisMonth}</p>
            <span className="text-[11px] text-indigo-500 font-medium">Current billing cycle</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
            <SafeIcon icon={FiTrendingUp} className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Quick Launch Certificate Types Carousel / Grid */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiZap} className="w-4 h-4 text-amber-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Popular Statutory Certificate Generators
            </h2>
          </div>
          <button
            onClick={() => navigate('/forms')}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-semibold flex items-center gap-1"
          >
            <span>View All ({availableForms.length})</span>
            <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {availableForms.slice(0, 4).map((form) => (
            <button
              key={form.id}
              onClick={() => navigate(`/forms/${form.id}`)}
              className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md transition-all text-left flex items-start gap-3 group"
            >
              <span className="text-2xl">{form.icon}</span>
              <div className="min-w-0 flex-1">
                <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
                  {form.name}
                </h4>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                  {form.description}
                </p>
                <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                  {form.category}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* High-Density Data Grid: Recent Records */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiLayers} className="w-4 h-4 text-blue-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Recent Activity & Certificate Registry
            </h2>
          </div>
        </div>

        <HighDensityDataGrid
          records={records}
          loading={loading}
          onInspectRecord={handleInspectRecord}
          onEditRecord={handleEditRecord}
          onDeleteRecord={handleDeleteRecord}
          onBatchStatusChange={handleBatchStatusChange}
          onBatchDelete={handleBatchDelete}
          onRefresh={loadDashboardData}
        />
      </div>

      {/* Quick View Slide-Over Drawer */}
      <CertificateQuickViewDrawer
        certificate={selectedCertificate}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onStatusChange={async (id, status) => {
          await FormService.updateFormStatus(id, status);
          if (selectedCertificate) {
            setSelectedCertificate({ ...selectedCertificate, status });
          }
          loadDashboardData();
        }}
        onDelete={async (id) => {
          await FormService.deleteForm(id);
          setIsDrawerOpen(false);
          loadDashboardData();
        }}
      />
    </div>
  );
};

export default Dashboard;
